/**
 * WCAG-correct colours for a coloured badge.
 *
 * `getReadableColor` picks black or white text from a brightness heuristic
 * (0.299r + 0.587g + 0.114b > 186). That is not a contrast calculation: the
 * brand teal #5e8d93 scores 127, so it got white text at a contrast ratio of
 * 3.67:1 — below the 4.5:1 that WCAG AA requires for small text, and the badge
 * label is 10px. Lighthouse reported it as the first failure on every card.
 *
 * This walks the background towards black (or white, for light colours) until
 * the chosen text colour clears the requested ratio, so the badge keeps its
 * brand hue and only gets darker.
 */

const clamp = v => Math.max(0, Math.min(255, Math.round(v)))

const parse = hex => {
  let h = String(hex || '').trim()
  if (h.startsWith('#')) h = h.slice(1)
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2]
  if (h.length !== 6) return null
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  if ([r, g, b].some(Number.isNaN)) return null
  return { r, g, b }
}

const toHex = ({ r, g, b }) =>
  `#${[r, g, b].map(c => clamp(c).toString(16).padStart(2, '0')).join('')}`

// WCAG 2.1 relative luminance.
const luminance = ({ r, g, b }) => {
  const channel = c => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

export const contrastRatio = (a, b) => {
  const la = luminance(a)
  const lb = luminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

const DARK_TEXT = { r: 0x2d, g: 0x37, b: 0x48 }
const WHITE = { r: 255, g: 255, b: 255 }

/**
 * @param {string} hex   the desired background (brand colour)
 * @param {number} ratio minimum contrast for the label, default AA small text
 * @returns {{bg: string, color: string}}
 */
const getAccessibleBadgeColors = (hex, ratio = 4.5) => {
  const base = parse(hex)
  if (!base) return { bg: hex, color: '#ffffff' }

  // Prefer white text; fall back to the dark ink only if it does better.
  const pickText = bg =>
    contrastRatio(bg, WHITE) >= contrastRatio(bg, DARK_TEXT) ? WHITE : DARK_TEXT

  let work = { ...base }
  const text = pickText(work)

  // Darken (or lighten, when the text ended up dark) until the ratio passes.
  for (let i = 0; i < 40 && contrastRatio(work, text) < ratio; i += 1) {
    if (text === WHITE) {
      work = { r: work.r * 0.94, g: work.g * 0.94, b: work.b * 0.94 }
    } else {
      work = {
        r: work.r + (255 - work.r) * 0.06,
        g: work.g + (255 - work.g) * 0.06,
        b: work.b + (255 - work.b) * 0.06
      }
    }
  }

  return { bg: toHex(work), color: toHex(text) }
}

export default getAccessibleBadgeColors
